import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Menu,
  MenuListItem,
  Input,
  INPUT_SIZES,
  IconArrowDropUp,
  IconArrowDropDown,
} from 'cdk-radial';
import './DAABSubscriptions.scss';

const StyledInput = styled(Input)`
  cursor: pointer;
  svg {
    cursor: pointer;
  }
`;

const DMSDropdown = ({ id, dmsType, dmsOptions, selectedDms, onDMSSelect }) => {
  const selectedIds = new Set(selectedDms.map((d) => d.id));
  const options = dmsOptions?.map((dms) => {
    return { label: dms.name, value: dms.name, data: dms };
  });
  const initSelections = options.filter((op) => selectedIds.has(op.data.id));

  const targetRef = useRef();
  const dimensionRef = useRef();
  const menuContainerRef = useRef();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState(initSelections);

  const isAllSelected =
    options.length && selectedOptions.length === options.length;
  const isAnySelected = selectedOptions.length > 0;
  const isIndeterminate = !isAllSelected && isAnySelected;

  const handleSelect = (option) => {
    const newSelectedOptions = selectedOptions.filter(
      (opt) => opt.value !== option.value
    );
    const isAlreadySelected =
      newSelectedOptions.length !== selectedOptions.length;
    if (isAlreadySelected) {
      const values = newSelectedOptions?.map((op) => ({ ...op.data, dmsType }));
      setSelectedOptions(newSelectedOptions);
      onDMSSelect(id, dmsType, values);
    } else {
      const updatedSelectedOptions = [option, ...selectedOptions];
      const values = updatedSelectedOptions?.map((op) => ({
        ...op.data,
        dmsType,
      }));
      setSelectedOptions(updatedSelectedOptions);
      onDMSSelect(id, dmsType, values);
    }
  };

  const handleAllSelect = () => {
    if (!isAnySelected) {
      const optionsExcludingDisabled = options.filter(
        (option) => !option.isDisabled
      );
      setSelectedOptions(optionsExcludingDisabled);
      const values = optionsExcludingDisabled?.map((op) => ({
        ...op.data,
        dmsType,
      }));
      onDMSSelect(id, dmsType, values);
      return;
    }
    setSelectedOptions([]);
    onDMSSelect(id, dmsType, []);
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const getIsOptionSelected = (option) => {
    return selectedOptions.some((opt) => opt.value === option.value);
  };

  const getValue = () => {
    if (isIndeterminate) {
      return selectedOptions.length === 1
        ? selectedOptions[0].label
        : `${selectedOptions.length} selected`;
    }
    if (isAllSelected) {
      return options.length > 1 ? 'All Selected' : selectedOptions[0].label;
    }
  };

  useEffect(() => {
    const setIsOpenFromMenu = (event) => {
      if (event.detail.ref === menuContainerRef)
        setIsOpen(event.detail.openMenu);
    };
    document.addEventListener('customMenuOpenEvent', setIsOpenFromMenu);
    return () =>
      document.removeEventListener('customMenuOpenEvent', setIsOpenFromMenu);
  }, []);

  return (
    <>
      <div style={{ width: '224px' }} data-testid="dms-multi-select-dropdown">
        <StyledInput
          dataTestId="dms-menu-input"
          icon={isOpen ? <IconArrowDropUp /> : <IconArrowDropDown />}
          id={`${id}-${dmsType}`}
          label={''}
          labelRef={dimensionRef}
          name={`${id}-${dmsType}`}
          onChange={() => {}}
          placeholder="DMS"
          ref={targetRef}
          value={getValue() || ''}
          size={INPUT_SIZES.SMALL}
          style={{ caretColor: 'transparent' }}
        />
      </div>
      <Menu
        dataTestId="dms-menu-container"
        dimensionRef={dimensionRef}
        isOpen={isOpen}
        isAuto={true}
        labelRef={targetRef}
        multi
        onClose={handleClose}
        onOpen={handleOpen}
        style={{ width: '224px', zIndex: '10000' }}
        ref={menuContainerRef}
      >
        <MenuListItem
          onClick={handleAllSelect}
          isSelected={isAllSelected || isIndeterminate}
          indeterminate={isIndeterminate}
          showDivider={options.length}
          hideSelectedItemCheckmark={false}
          isDisabled={options.length === 0}
          dataTestId={`select-all-${dmsType}`}
        >
          Select All
        </MenuListItem>
        {options.map((option, ind) => {
          return (
            <MenuListItem
              key={ind}
              isSelected={getIsOptionSelected(option)}
              onClick={() => handleSelect(option)}
              isDisabled={option.disabled}
              showDivider={option.showDivider}
              hideSelectedItemCheckmark={false}
            >
              {option.label}
            </MenuListItem>
          );
        })}
      </Menu>
    </>
  );
};

export { DMSDropdown };
